import React, { useContext, useEffect, useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Button,
	TextField,
	Divider,
	IconButton,
	getLuminance,
	darken,
	lighten,
	alpha,
	CircularProgress,
	Modal,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../contexts/UserContext.js";
import AsyncStorage from "@react-native-async-storage/async-storage";
import ConnectionsClient from "../../services/ConnectionsClient.js";
import { useToast } from "../../contexts/ToastContext.js";
import { CreatorContext } from "../../contexts/CreatorContext.js";
import CloseIcon from "@mui/icons-material/Close";
import Login from "../Login/Login.js";
import CustomPhoneInput from "../CustomPhoneInput/CustomPhoneInput.js";

function Confirmation() {
	const { t } = useTranslation();
	const { state } = useLocation();
	const {
		selectedService,
		selectedOptions,
		selectedWorker,
		selectedDate,
		selectedTime,
	} = state;

	const navigate = useNavigate();
	const { basePath } = useParams();

	const { user, logout } = useContext(UserContext);

	const [name, setName] = useState(user?.name || "");
	const [email, setEmail] = useState(user?.email || "");
	const [phone, setPhone] = useState(
		user && user.phonePrefix && user.phone
			? user.phonePrefix + user.phone
			: "+34"
	);
	const [acceptTerms, setAcceptTerms] = useState(false);

	const [openDialog, setOpenDialog] = useState(false);
	const [loginRoute, setLoginRoute] = useState("login");
	const { showMessage } = useToast();

	const [loading, setLoading] = useState(false);

	const { creator, creatorWorkers, creatorServices } =
		useContext(CreatorContext);

	useEffect(() => {
		if (user) {
			setName(user?.name);
			setEmail(user?.email);
			setPhone(
				user && user.phone ? (user.phonePrefix ?? "+34") + user.phone : ""
			);
		} else {
			setName("");
			setEmail("");
			setPhone("+34");
		}
	}, [user]);

	// ✅ Función para validar el email
	const isValidEmail = (email) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	};

	// ✅ Función para validar el teléfono (mínimo 7 dígitos numéricos)
	const isValidPhone = (phone) => {
		return /^\d{7,15}$/.test(phone.replace(/\D/g, "")); // Elimina caracteres no numéricos
	};

	// ✅ Botón solo se activa si TODOS los datos están correctos
	const isFormValid = () => {
		return (
			acceptTerms &&
			(user ||
				(name.trim() !== "" && isValidEmail(email) && isValidPhone(phone)))
		);
	};

	// Función para validar el correo electrónico
	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(String(email).toLowerCase());
	};

	const handlePhoneChange = (value) => {
		setPhone(value); // Actualiza el valor sin perder foco
	};

	// Abrir la modal de confirmación
	const handleOpenDialog = (appointment) => {
		setOpenDialog(true); // Abrimos la modal
	};

	// Cerrar la modal de confirmación
	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleLogin = () => {
		handleOpenDialog();
	};

	const getFormattedPriceWithDiscount = (price, discount) => {
		if (discount && discount > 0) {
			const discountedPrice = price - (price * discount) / 100;
			return (
				<>
					{/* Precio original tachado */}
					<span
						style={{
							textDecoration: "line-through",
							color: "gray",
							marginRight: "8px",
						}}
					>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(price)}
					</span>
					{/* Precio con descuento en rojo */}
					<span style={{ color: "red", fontWeight: "bold" }}>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(discountedPrice)}
					</span>
				</>
			);
		}

		// Si no hay descuento, devuelve solo el precio normal
		return new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(price);
	};

	const makeAppointment = async () => {
		if (user && !user?.activatedAt) {
			setOpenDialog(true);
			setLoginRoute("activate");
		} else {
			setLoading(true);

			try {
				let headers = {};

				// Llamada a la API con ConnectionsClient
				if (!creator.isCreator) {
					let endpoint = "appointment";
					const params = {
						title: selectedOptions
							? selectedOptions.title
							: selectedService.title,
						serviceId: selectedService.id,
						serviceOptionId: selectedOptions ? selectedOptions.id : null,
						date: selectedDate,
						hour: selectedTime,
						workerId: selectedWorker ? selectedWorker.id : null,
						couponId: null,
						//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
					};

					if (!user) {
						params.userName = name;
						params.userEmail = email;
						params.phone = phone;
						endpoint = "/appointment-anonymous";
					} else {
						const token = await AsyncStorage.getItem("token");
						headers = token ? { authorization: token, "x-wc-call": true } : {};
					}

					await ConnectionsClient.post(endpoint, params, {
						headers: headers,
					});
				} else {
					let appointments = await AsyncStorage.getItem("appointments");
					if (appointments === null) {
						appointments = [];
					} else {
						appointments = JSON.parse(appointments);
					}

					const auxUser = user
						? { ...user }
						: {
								userName: name,
								userEmail: email,
								phone: phone,
						  };

					appointments.push({
						id: new Date().getTime(),
						title: selectedOptions
							? selectedOptions.title
							: selectedService.title,
						date: selectedDate + " " + selectedTime,
						hour: selectedTime,
						createdAt: new Date().toISOString(),
						updatedAt: new Date().toISOString(),
						workerId: selectedWorker ? selectedWorker.id : null,
						serviceId: selectedService.id,
						serviceOptionId: selectedOptions ? selectedOptions.id : null,
						couponId: null,
						worker: creatorWorkers.find(
							(worker) =>
								worker.id === (selectedWorker ? selectedWorker.id : null)
						),
						service: creatorServices.find(
							(service) => service.id === selectedService.id
						),
						serviceOption:
							creatorServices
								.find((service) => service.id === selectedService.id)
								?.serviceOptions.find(
									(serviceOption) =>
										serviceOption.id === (selectedOptions?.id ?? null)
								) ?? null,
						user: { ...auxUser },
						coupon: null,
					});
					await AsyncStorage.setItem(
						"appointments",
						JSON.stringify(appointments)
					);
				}

				setLoading(false);

				navigate(`/${basePath}/appointment-confirmed`, {
					state: {
						selectedService,
						selectedOptions,
						selectedWorker,
						selectedDate,
						selectedTime,
					},
				});
			} catch (error) {
				console.log(error);
				setLoading(false);
				showMessage({
					message: t("errors.somethingWentWrong"),
					type: "danger",
					icon: "danger",
				});
			}
		}
	};

	const handleConfirm = () => {
		if (name && email && phone) {
			makeAppointment();
		} else {
			alert(t("pleaseCompleteFields"));
		}
	};

	return (
		<Box
			sx={{
				padding: { xs: "0px 16px", sm: "0px 30px" },
				backgroundColor: creator?.backgroundColor || "#ffffff",
				color: creator?.fontColor || "#000000",
				overflowX: "hidden",
				height: "calc(100% - 40px)!important",
			}}
		>
			<Box
				sx={{
					display: "block",
					gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
					gap: "30px",
				}}
			>
				{/* Columna izquierda: Resumen de la cita */}
				<Box>
					{/* Encabezado */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							marginBottom: "12px",
						}}
					>
						<IconButton
							onClick={() => navigate(-1)}
							sx={{
								color: (getLuminance(creator?.backgroundColor ?? "#FFFFFF") >=
									0.5
									? darken
									: lighten)(creator?.fontColor ?? "#000000", 0.8),
								padding: "4px",
							}}
						>
							<IconArrowLeft size={20} />
						</IconButton>

						<Typography
							variant="h4"
							sx={{
								fontSize: "15px",
								fontWeight: 600,
								letterSpacing: "-0.2px",
							}}
						>
							{t("confirmYourAppointment")}
						</Typography>
					</Box>

					{/* Tarjeta resumen compacta */}
					<Paper
						sx={{
							padding: "14px 16px",
							backgroundColor: creator?.backgroundColor ?? "#FFFFFF",
							border: !creator?.showShadows
								? "1px solid " + alpha(creator?.fontColor ?? "#000000", 0.15)
								: "none",
							boxShadow: creator?.showShadows
								? getLuminance(creator?.backgroundColor ?? "#FFFFFF") >= 0.5
									? "0px 2px 8px rgba(0, 0, 0, 0.05)"
									: "0px 2px 8px rgba(255, 255, 255, 0.25)"
								: "none",
							borderRadius: "10px",
							color: creator?.fontColor ?? "#000000",
						}}
					>
						{/* Primera fila: nombre + hora */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
								marginBottom: "6px",
							}}
						>
							<Typography
								sx={{
									fontSize: "14px",
									fontWeight: 600,
									flex: 1,
									marginRight: "10px",
								}}
							>
								{selectedOptions
									? `${selectedService.title} - ${selectedOptions.title}`
									: selectedService.title}
							</Typography>
							<Typography
								variant="body2"
								sx={{ fontSize: "13px", opacity: 0.8 }}
							>
								{new Date(selectedDate).toLocaleDateString("es-ES", {
									weekday: "short",
									day: "numeric",
									month: "short",
								})}
							</Typography>
						</Box>

						{/* Segunda fila: fecha + trabajador */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: "6px",
							}}
						>
							<Typography
								variant="body2"
								sx={{ fontSize: "13px", opacity: 0.8 }}
							>
								{t("withWorker", { workerName: selectedWorker.name })}
							</Typography>

							<Typography
								sx={{
									fontSize: "15px",
									fontWeight: 700,
									color: creator?.mainColor ?? "#43BD98",
								}}
							>
								{selectedTime}h
							</Typography>
						</Box>

						<Divider
							sx={{
								borderColor: alpha(creator?.fontColor ?? "#000000", 0.1),
								marginY: 1.5,
							}}
						/>

						{/* Total */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
								{t("totalToPay")}
							</Typography>
							<Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
								{selectedOptions
									? getFormattedPriceWithDiscount(
											selectedOptions.price,
											selectedOptions.discount
									  )
									: getFormattedPriceWithDiscount(
											selectedService.price,
											selectedService.discount
									  )}
							</Typography>
						</Box>
					</Paper>

					{/* Enlace alternar hora */}
					<Box
						sx={{
							textAlign: "center",
							marginTop: 1.5,
							display: { xs: "block", sm: "block" },
						}}
					>
						<Button
							variant="text"
							sx={{
								textDecoration: "underline",
								fontSize: "13px",
								color: creator?.mainColor || "#43BD98",
								fontWeight: 500,
								padding: 0,
							}}
							onClick={() => {
								if (user) {
									logout();
								} else {
									handleLogin();
								}
							}}
						>
							{t(user ? "useOtherUser" : "signIn")}
						</Button>
					</Box>
				</Box>

				{/* Columna derecha: Datos del usuario y botones */}
				<Box>
					<Box
						sx={{
							"& .selected-flag:hover": {
								background: creator?.backgroundColor ?? "#FFFFFF",
							},
							"& .selected-flag:focus": {
								background: creator?.backgroundColor ?? "#FFFFFF",
							},
							"& .flag-dropdown.open .selected-flag": {
								background: creator?.backgroundColor ?? "#FFFFFF",
							},
							".country-list": {
								background: creator?.backgroundColor ?? "#FFFFFF",
							},
							".country-list .country:hover": {
								background: alpha(creator?.fontColor ?? "#000000", 0.2),
							},
							".country-list .country.highlight": {
								background: alpha(creator?.fontColor ?? "#000000", 0.25),
							},
							"& input:disabled": {
								borderColor:
									alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
								color:
									alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
							},
							"& input:disabled + .flag-dropdown": {
								borderColor:
									alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
								color:
									alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
							},
						}}
					>
						<>
							<Typography
								variant="h4"
								gutterBottom
								sx={{
									fontSize: "16px",
									fontWeight: 600,
									marginBottom: 2,
									marginTop: 2,
								}}
							>
								{!user ? t("yourData") : t("enterYourData")}
							</Typography>
							<Box
								sx={{
									//maxHeight: "calc(100vh - 400px)",
									//overflowY: "auto",
									WebkitOverflowScrolling: "touch",
									paddingRight: "4px",
								}}
							>
								<TextField
									placeholder={t("name")}
									fullWidth
									value={name}
									onChange={(e) => setName(e.target.value)}
									sx={{
										marginBottom: "16px", // Margen consistente con otros inputs
										"& fieldset": {
											borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
										},
										"& .Mui-focused fieldset": {
											borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
										},
										"& input": {
											color: creator?.fontColor ?? "#000000", // Color de texto normal
											WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
										},
										"& .MuiInputBase-root:hover fieldset": {
											borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
										},
										"& input:not(.Mui-disabled)": {
											color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
											WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
										},
										"& .Mui-disabled + fieldset": {
											borderColor:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										"& input.Mui-disabled": {
											color:
												alpha(creator?.fontColor ?? "#000000", 0.3) +
												"!important",
											WebkitTextFillColor:
												alpha(creator?.fontColor ?? "#000000", 0.3) +
												"!important",
										},
										"& .MuiInputBase-root:hover": {
											borderColor: creator?.mainColor ?? "#43BD98",
										},
										fontSize: "14px",
									}}
									inputProps={{
										sx: {
											"&::placeholder": {
												fontSize: "14px",
												color:
													alpha(creator?.fontColor ?? "#000000", 0.2) +
													"!important",
											},
											color: creator?.fontColor ?? "#000000",
											fontSize: "14px",
											"&:-webkit-autofill": {
												color: (creator?.fontColor ?? "#000000") + "!important",
												WebkitTextFillColor:
													(creator?.fontColor ?? "#000000") + "!important",
												backgroundColor:
													(creator?.backgroundColor ?? "#ffffff") +
													" !important",
												WebkitBoxShadow:
													"0 0 0 1000px " +
													(creator?.backgroundColor ?? "#ffffff") +
													" inset",
												fontSize: "14px",
												font: creator?.fontFamily ?? "Montserrat",
												fontFamily: `'${
													creator?.fontFamily ?? "Montserrat"
												}', sans-serif !important`,
											},
										},
									}}
									disabled={user ? true : false}
								/>
								<TextField
									placeholder={t("email")}
									fullWidth
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									error={email && !validateEmail(email)}
									helperText={
										email && !validateEmail(email) ? t("invalidEmail") : ""
									}
									sx={{
										marginBottom: "16px", // Margen consistente con otros inputs
										"& fieldset": {
											borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
										},
										"& .Mui-focused fieldset": {
											borderColor: alpha(creator?.mainColor ?? "#000000", 0.7), // Borde con transparencia al estar enfocado
										},
										"& input": {
											color: creator?.fontColor ?? "#000000", // Color de texto normal
											WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
										},
										"& .MuiInputBase-root:hover fieldset": {
											borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
										},
										"& input:not(.Mui-disabled)": {
											color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
											WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
										},
										"& .Mui-disabled + fieldset": {
											borderColor:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										"& input.Mui-disabled": {
											color:
												alpha(creator?.fontColor ?? "#000000", 0.3) +
												"!important",
											WebkitTextFillColor:
												alpha(creator?.fontColor ?? "#000000", 0.3) +
												"!important",
										},
										"& .MuiInputBase-root:hover": {
											borderColor: creator?.mainColor ?? "#43BD98",
										},
										fontSize: "14px",
									}}
									inputProps={{
										sx: {
											"&::placeholder": {
												fontSize: "14px",
												color:
													alpha(creator?.fontColor ?? "#000000", 0.2) +
													"!important",
											},
											"&:-webkit-autofill": {
												color: (creator?.fontColor ?? "#000000") + "!important",
												WebkitTextFillColor:
													(creator?.fontColor ?? "#000000") + "!important",
												backgroundColor:
													(creator?.backgroundColor ?? "#ffffff") +
													" !important",
												WebkitBoxShadow:
													"0 0 0 1000px " +
													(creator?.backgroundColor ?? "#ffffff") +
													" inset",
												fontSize: "14px",
												font: creator?.fontFamily ?? "Montserrat",
												fontFamily: `'${
													creator?.fontFamily ?? "Montserrat"
												}', sans-serif !important`,
											},
											color: creator?.fontColor ?? "#000000",
											fontSize: "14px",
										},
									}}
									disabled={user ? true : false}
								/>
								<CustomPhoneInput
									country={"es"}
									value={phone}
									onChange={handlePhoneChange}
									inputStyle={{
										width: "100%", // Asegurar que ocupe todo el ancho
										fontSize: "14px", // Tamaño de fuente del input
										fontFamily: "Montserrat, sans-serif", // Cambiar la fuente a Montserrat
										height: "56px", // Ajustar la altura del input para coincidir con otros inputs
										paddingLeft: "70px", // Aumentar el padding para evitar el solapamiento del prefijo
										marginBottom: "16px", // Asegurar margen consistente con otros inputs
										background: creator?.backgroundColor ?? "#FFFFFF",
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
										color: creator?.fontColor ?? "#000000",
									}}
									buttonStyle={{
										width: "56px", // Ajustar el ancho del área de la bandera
										height: "56px", // Asegurar que la bandera tenga la misma altura que el input
										background: creator?.backgroundColor ?? "#FFFFFF",
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									}}
									dropdownStyle={{
										fontFamily: "Montserrat, sans-serif", // Cambiar la fuente de la lista desplegable
										fontSize: "14px", // Tamaño de fuente en el dropdown
									}}
									disabled={user ? true : false}
								/>
								{/* Checkbox obligatorio para aceptar términos */}
								<FormControlLabel
									control={
										<Checkbox
											checked={acceptTerms}
											onChange={(e) => setAcceptTerms(e.target.checked)}
											color="primary"
											sx={{
												color: acceptTerms
													? creator.color
													: alpha(creator?.fontColor ?? "#000000", 0.2) +
													  "!important",
											}}
										/>
									}
									sx={{
										marginTop: 2,
										fontSize: "14px",
										"& .MuiFormControlLabel-label": {
											fontSize: "14px",
										},
									}}
									label={
										<>
											{t("terms")}{" "}
											<a
												href={creator?.termsURL}
												target="_blank"
												rel="noreferrer"
												onClick={(e) => e.stopPropagation()}
												style={{
													color: creator?.mainColor ?? "#ffffff",
												}}
											>
												{t("terms2")}
											</a>{" "}
											{t("terms3")}{" "}
											<a
												href={creator?.policyURL}
												target="_blank"
												rel="noreferrer"
												onClick={(e) => e.stopPropagation()}
												style={{
													color: creator?.mainColor ?? "#ffffff",
												}}
											>
												{t("terms4")}
											</a>
										</>
									}
								/>
							</Box>
						</>
					</Box>
				</Box>
			</Box>
			{/* Botón Confirmar */}
			<Box
				sx={{
					position: "fixed",
					width: "calc(100% - 40px)",
					bottom: 0,
					right: 0,
					padding: "20px",
					textAlign: "right",
					backgroundColor: creator?.backgroundColor || "#ffffff",
				}}
			>
				<Button
					variant="contained"
					color="primary"
					onClick={handleConfirm}
					disabled={
						!name ||
						!email ||
						!phone ||
						!acceptTerms ||
						loading ||
						!isFormValid()
					} // Deshabilitar si no está logueado o si faltan campos
					sx={{
						padding: "10px 20px",
						opacity:
							!name || !email || !phone || !acceptTerms || !isFormValid()
								? 0.5
								: 1, // Reducir opacidad si está deshabilitado
						width: { xs: "100%", sm: "auto" },
						minHeight: 40.5,
						color: loading ? "transparent!important" : undefined,
						"&.Mui-disabled": {
							opacity: 0.5,
							color: loading
								? "transparent!important"
								: getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
								? "#000000"
								: "#FFFFFF", // Texto de los botones primarios
							backgroundColor: creator?.mainColor ?? "#43BD98",
						},
					}}
				>
					{t("confirmAppointment")}
					{loading && (
						<CircularProgress
							size={12}
							sx={{ position: "absolute", top: 16, color: "white" }}
						/>
					)}
				</Button>
			</Box>
			<Modal
				open={openDialog}
				onClose={handleCloseDialog}
				sx={{
					display: "flex",
					alignItems: { xs: "center", sm: "center" }, // centra verticalmente en todas
					justifyContent: "center",
					p: { xs: 0, sm: 2, md: 4 },
					"& .MuiModal-backdrop": {
						backgroundColor:
							getLuminance(creator?.backgroundColor ?? "#FFFFFF") >= 0.5
								? "rgba(0, 0, 0, 0.5)"
								: "rgba(0, 0, 0, 0.9)",
					},
				}}
			>
				<Box
					sx={{
						position: "relative",
						width: { xs: "100%", sm: "480px" },
						maxWidth: "100%",
						maxHeight: "90vh", // evita desbordes
						backgroundColor: creator?.backgroundColor ?? "#FFFFFF",
						borderRadius: { xs: 0, sm: 2 },
						p: { xs: "12px", sm: "24px" },
						boxSizing: "border-box",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center", // centra vertical y horizontalmente
						margin: "auto", // 👈 asegura centrado real
					}}
				>
					<IconButton
						onClick={handleCloseDialog}
						sx={{
							position: "absolute",
							top: { xs: "8px", sm: "24px" },
							right: { xs: "8px", sm: "24px" },
							color: creator?.fontColor ?? "#000000",
							zIndex: 10,
						}}
					>
						<CloseIcon />
					</IconButton>

					<Login
						inModal={true}
						callback={() => {
							handleCloseDialog();
						}}
						loginRoute={loginRoute}
					/>
				</Box>
			</Modal>
		</Box>
	);
}

export default Confirmation;
